import React from 'react';
import { Link } from 'react-router-dom';
import LoaderIcon from 'uswds/dist/img/loader.svg';
import { DropdownControl, Fieldset, Form, TextInputControl } from '@ver-uds/uswds-react';
import { Button, Checkbox } from '@ver-uds/react';
import { useTypedController } from '@hookform/strictly-typed';
import { Address, Location } from 'eve-account-client';
import { useForm } from 'react-hook-form';
import EnrollmentStepperButtons from '../EnrollmentStepperButtons';
import { useCountiesQuery, useStatesQuery } from '../../../services';
import FormVariant from '../FormVariant';
import { CompanyAddressesFormData } from './CompanyAddressesFormData';

export interface CompanyAddressesFormProps {
  variantType: FormVariant;
  companyAddressesData?: CompanyAddressesFormData;
  cancelUrl?: string;
  onSubmitForm: (data: CompanyAddressesFormData) => void;
}

export type FormValues = {
  physicalLocation: Required<Omit<Location, 'countyId'> & { countyId: Location['countyId'] | '' }>;
  mailingAddressIsSame: boolean;
  mailingAddress: Required<Address>;
};

const CompanyAddressesForm = ({
  variantType,
  companyAddressesData,
  cancelUrl,
  onSubmitForm,
}: CompanyAddressesFormProps): JSX.Element => {
  const { isLoading: statesIsLoading, data: statesData } = useStatesQuery();

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { register, handleSubmit, errors, setValue, watch, control, clearErrors } = useForm<FormValues>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      physicalLocation: {
        line1: companyAddressesData?.physicalLocation?.line1 ?? '',
        line2: companyAddressesData?.physicalLocation?.line2 ?? '',
        city: companyAddressesData?.physicalLocation?.city ?? '',
        stateCode: companyAddressesData?.physicalLocation?.stateCode ?? '',
        zipCode: companyAddressesData?.physicalLocation?.zipCode ?? '',
        countyId: companyAddressesData?.physicalLocation?.countyId ?? '',
      },
      mailingAddressIsSame:
        (variantType === FormVariant.EDIT && !companyAddressesData?.mailingAddress) ||
        (variantType === FormVariant.ENROLLMENT && companyAddressesData?.mailingAddressIsSame),
      mailingAddress: {
        line1: companyAddressesData?.mailingAddress?.line1 ?? '',
        line2: companyAddressesData?.mailingAddress?.line2 ?? '',
        city: companyAddressesData?.mailingAddress?.city ?? '',
        stateCode: companyAddressesData?.mailingAddress?.stateCode ?? '',
        zipCode: companyAddressesData?.mailingAddress?.zipCode ?? '',
      },
    },
  });

  const TypedController = useTypedController<FormValues>({ control });
  const { physicalLocation, mailingAddressIsSame, mailingAddress } = watch();

  const { isLoading: physicalLocationCountiesIsLoading, data: physicalLocationCountiesData } = useCountiesQuery(
    physicalLocation.stateCode,
  );

  const handleSubmitForm = handleSubmit((data: FormValues) => {
    const submitData = {
      physicalLocation: data.physicalLocation as Location,
      mailingAddressIsSame: data.mailingAddressIsSame,
      mailingAddress: data.mailingAddressIsSame ? undefined : data.mailingAddress,
    } as CompanyAddressesFormData;
    onSubmitForm(submitData);
  });

  return (
    <>
      {statesIsLoading ? (
        <div className="margin-top-5 margin-bottom-5">
          Loading… <img src={LoaderIcon} alt="" className="height-2" />
        </div>
      ) : (
        <Form className="maxw-none" onSubmit={handleSubmitForm} noValidate>
          <Fieldset className="margin-top-4 margin-bottom-5">
            <h2>Physical Address</h2>
            <TextInputControl
              id="physical-location-line1"
              name="physicalLocation.line1"
              className="grid-col-12 tablet:grid-col-9 desktop:grid-col-6"
              label="Street Address"
              required
              ref={register({
                required: {
                  value: true,
                  message: 'Street Address is required.',
                },
                maxLength: {
                  value: 60,
                  message: 'Please enter no more than 60 characters.',
                },
                pattern: {
                  value: /^[^()|'‘’‛`]*$/,
                  message: 'This field cannot contain apostrophes, parentheses, or pipes.',
                },
              })}
              errorId={errors.physicalLocation?.line1 && 'physical-location-line1-error'}
              errorMessage={errors.physicalLocation?.line1?.message}
            />

            <TextInputControl
              id="physical-location-line2"
              name="physicalLocation.line2"
              className="grid-col-12 tablet:grid-col-9 desktop:grid-col-6"
              label="Suite/Other"
              ref={register({
                maxLength: {
                  value: 60,
                  message: 'Please enter no more than 60 characters.',
                },
                pattern: {
                  value: /^[^()|'‘’‛`]*$/,
                  message: 'This field cannot contain apostrophes, parentheses, or pipes.',
                },
              })}
              errorId={errors.physicalLocation?.line2 && 'physical-location-line2-error'}
              errorMessage={errors.physicalLocation?.line2?.message}
            />

            <TextInputControl
              id="physical-location-city"
              name="physicalLocation.city"
              className="grid-col-12 tablet:grid-col-5 desktop:grid-col-3"
              label="City"
              required
              ref={register({
                required: {
                  value: true,
                  message: 'City is required.',
                },
                maxLength: {
                  value: 30,
                  message: 'Please enter no more than 30 characters.',
                },
                pattern: {
                  value: /^[a-zA-Z0-9\-‒–—― ]*$/,
                  message: 'This field cannot contain special characters, aside from dashes and spaces.',
                },
              })}
              errorId={errors.physicalLocation?.city && 'physical-location-city-error'}
              errorMessage={errors.physicalLocation?.city?.message}
            />

            <DropdownControl
              id="physical-location-state-code"
              name="physicalLocation.stateCode"
              className="grid-col-12 tablet:grid-col-5 desktop:grid-col-3"
              label="State"
              required
              ref={register({
                required: {
                  value: true,
                  message: 'State is required.',
                },
              })}
              onChange={() => setValue('physicalLocation.countyId', '', { shouldValidate: false, shouldDirty: true })}
              errorId={errors.physicalLocation?.stateCode && 'physical-location-state-code-error'}
              errorMessage={errors.physicalLocation?.stateCode?.message}
            >
              <option value="">- Select -</option>
              {statesData?.map((state) => (
                <option key={state.code} value={state.code}>
                  {state.displayName}
                </option>
              ))}
            </DropdownControl>

            <TextInputControl
              id="physical-location-zip-code"
              name="physicalLocation.zipCode"
              className="grid-col-12 tablet:grid-col-5 desktop:grid-col-3"
              label="Zip Code"
              hint="5 digits, numbers only"
              required
              ref={register({
                validate: (value: string) => {
                  if (!/^\d{5}$/.exec(value)) {
                    return 'Please enter a 5-digit Zip Code.';
                  }
                  return true;
                },
              })}
              errorId={errors.physicalLocation?.zipCode && 'physical-location-zip-code-error'}
              errorMessage={errors.physicalLocation?.zipCode?.message}
            />

            <DropdownControl
              id="physical-location-county-id"
              name="physicalLocation.countyId"
              className="grid-col-12 tablet:grid-col-5 desktop:grid-col-3"
              label="County"
              required
              ref={
                physicalLocationCountiesIsLoading
                  ? null
                  : register({
                      required: {
                        value: true,
                        message: 'County is required.',
                      },
                    })
              }
              errorId={errors.physicalLocation?.countyId && 'physical-location-county-id-error'}
              errorMessage={errors.physicalLocation?.countyId?.message}
            >
              <option value="">- Select -</option>
              {physicalLocationCountiesData?.map((county) => (
                <option key={county.id} value={county.id}>
                  {county.displayName}
                </option>
              ))}
            </DropdownControl>
          </Fieldset>
          <Fieldset className="margin-top-4 margin-bottom-5">
            <h2>Mailing Address</h2>
            <Checkbox
              id="mailing-address-is-same"
              className="margin-top-4"
              name="mailingAddressIsSame"
              label="Mailing Address is the same as Physical Address"
              ref={register()}
              onChange={(event) => {
                (Object.keys(mailingAddress) as (keyof Address)[]).forEach((fieldName) => {
                  const nestedFieldName = `mailingAddress.${fieldName}`;

                  if (event.target.checked) {
                    clearErrors(nestedFieldName);
                  } else {
                    setValue(nestedFieldName, physicalLocation[fieldName], {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  }
                });
              }}
            />

            <TypedController
              name={['mailingAddress', 'line1']}
              rules={{
                required: {
                  value: !mailingAddressIsSame,
                  message: 'Street Address is required.',
                },
                maxLength: {
                  value: 60,
                  message: 'Please enter no more than 60 characters.',
                },
                pattern: {
                  value: /^[^()|'‘’‛`]*$/,
                  message: 'This field cannot contain apostrophes, parentheses, or pipes.',
                },
              }}
              render={({ value, ...controllerProps }) => (
                <TextInputControl
                  id="mailing-address-line1"
                  className="grid-col-12 tablet:grid-col-9 desktop:grid-col-6"
                  label="Street Address"
                  disabled={mailingAddressIsSame}
                  required
                  errorId={errors.mailingAddress?.line1 && 'mailing-address-line1-error'}
                  errorMessage={errors.mailingAddress?.line1?.message}
                  value={mailingAddressIsSame ? physicalLocation.line1 : value}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...controllerProps}
                />
              )}
            />

            <TypedController
              name={['mailingAddress', 'line2']}
              rules={{
                maxLength: {
                  value: 60,
                  message: 'Please enter no more than 60 characters.',
                },
                pattern: {
                  value: /^[^()|'‘’‛`]*$/,
                  message: 'This field cannot contain apostrophes, parentheses, or pipes.',
                },
              }}
              render={({ value, ...controllerProps }) => (
                <TextInputControl
                  id="mailing-address-line2"
                  className="grid-col-12 tablet:grid-col-9 desktop:grid-col-6"
                  label="Suite/Other"
                  disabled={mailingAddressIsSame}
                  errorId={errors.mailingAddress?.line2 && 'mailing-address-line2-error'}
                  errorMessage={errors.mailingAddress?.line2?.message}
                  value={mailingAddressIsSame ? physicalLocation.line2 : value}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...controllerProps}
                />
              )}
            />

            <TypedController
              name={['mailingAddress', 'city']}
              rules={{
                required: {
                  value: !mailingAddressIsSame,
                  message: 'City is required.',
                },
                maxLength: {
                  value: 30,
                  message: 'Please enter no more than 30 characters.',
                },
                pattern: {
                  value: /^[a-zA-Z0-9\-‒–—― ]*$/,
                  message: 'This field cannot contain special characters, aside from dashes and spaces.',
                },
              }}
              render={({ value, ...controllerProps }) => (
                <TextInputControl
                  id="mailing-address-city"
                  className="grid-col-12 tablet:grid-col-5 desktop:grid-col-3"
                  label="City"
                  disabled={mailingAddressIsSame}
                  required
                  errorId={errors.mailingAddress?.city && 'mailing-address-city-error'}
                  errorMessage={errors.mailingAddress?.city?.message}
                  value={mailingAddressIsSame ? physicalLocation.city : value}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...controllerProps}
                />
              )}
            />

            <TypedController
              name={['mailingAddress', 'stateCode']}
              rules={{
                required: {
                  value: !mailingAddressIsSame,
                  message: 'State is required.',
                },
              }}
              render={({ value, ...controllerProps }) => (
                <DropdownControl
                  id="mailing-address-state-code"
                  className="grid-col-12 tablet:grid-col-5 desktop:grid-col-3"
                  label="State"
                  disabled={mailingAddressIsSame}
                  required
                  errorId={errors.mailingAddress?.stateCode && 'mailing-address-state-code-error'}
                  errorMessage={errors.mailingAddress?.stateCode?.message}
                  value={mailingAddressIsSame ? physicalLocation.stateCode : value}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...controllerProps}
                >
                  <option value="">- Select -</option>
                  {statesData?.map((state) => (
                    <option key={state.code} value={state.code}>
                      {state.displayName}
                    </option>
                  ))}
                </DropdownControl>
              )}
            />

            <TypedController
              name={['mailingAddress', 'zipCode']}
              rules={{
                validate: (value: string) => {
                  if (!mailingAddressIsSame && !/^\d{5}$/.exec(value)) {
                    return 'Please enter a 5-digit Zip Code.';
                  }
                  return true;
                },
              }}
              render={({ value, ...controllerProps }) => (
                <TextInputControl
                  id="mailing-address-zip-code"
                  className="grid-col-12 tablet:grid-col-5 desktop:grid-col-3"
                  label="Zip Code"
                  hint="5 digits, numbers only"
                  disabled={mailingAddressIsSame}
                  required
                  errorId={errors.mailingAddress?.zipCode && 'mailing-address-zip-code-error'}
                  errorMessage={errors.mailingAddress?.zipCode?.message}
                  value={mailingAddressIsSame ? physicalLocation.zipCode : value}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...controllerProps}
                />
              )}
            />
          </Fieldset>
          {variantType === FormVariant.ENROLLMENT ? (
            <EnrollmentStepperButtons nextButtonType="submit" />
          ) : (
            <>
              <Link to={cancelUrl || ''} className="usa-button usa-button--outline">
                Cancel
              </Link>
              <Button type="submit" variant="primary">
                Save
              </Button>
            </>
          )}
        </Form>
      )}
    </>
  );
};

export default CompanyAddressesForm;
